/**
 * SandboxBanner — persistent top banner with save/share/reset controls.
 */

import React, { useState } from 'react';
import { FlaskConical, RotateCcw, LogOut, Save, Share2, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSandboxSession } from './hooks/useSandboxSession';
import { useAuth } from '../contexts/AuthContext';
import SandboxSaveModal from './SandboxSaveModal';

export default function SandboxBanner() {
  const navigate = useNavigate();
  const { resetSandbox } = useSandboxSession();
  const { user } = useAuth();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleReset = () => {
    resetSandbox();
    window.location.reload();
  };

  const handleExit = () => {
    resetSandbox();
    navigate('/');
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/sandbox`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <div className="sb-banner" role="status" aria-live="polite">
        <div className="sb-banner-left">
          <FlaskConical size={16} className="sb-banner-icon" />
          <span className="sb-banner-label">SANDBOX MODE</span>
          <span className="sb-banner-sep" aria-hidden="true">&bull;</span>
          <span className="sb-banner-info">No data is saved</span>
          <span className="sb-banner-sep" aria-hidden="true">&bull;</span>
          <span className="sb-banner-info">Session resets on close</span>
        </div>
        <div className="sb-banner-right">
          {!user && (
            <button className="sb-banner-btn sb-banner-btn--save" onClick={() => setShowSaveModal(true)}>
              <Save size={14} />
              Save Your Work
            </button>
          )}
          <button className="sb-banner-btn" onClick={handleShare}>
            {copied ? <Check size={14} /> : <Share2 size={14} />}
            {copied ? 'Copied!' : 'Share'}
          </button>
          <button className="sb-banner-btn sb-banner-btn--reset" onClick={handleReset}>
            <RotateCcw size={14} />
            Reset
          </button>
          <button className="sb-banner-btn sb-banner-btn--exit" onClick={handleExit}>
            <LogOut size={14} />
            Exit
          </button>
        </div>
      </div>

      {showSaveModal && (
        <SandboxSaveModal
          onClose={() => setShowSaveModal(false)}
          onSaved={() => setShowSaveModal(false)}
        />
      )}
    </>
  );
}
